/**
 * pt-BR number inputs ↔ API units. Inputs are strings while typing ("30,00", "12,5", "0,95");
 * the API wants centavos, basis points or plain decimals.
 */

/** "1.249,90" | "1249.90" | "30" → number, or null if empty/invalid. */
export function parseDecimal(text: string): number | null {
  const t = text.trim().replace(/\s|R\$|%/g, '');
  if (t === '') return null;
  // pt-BR: dots are thousands separators when a comma is present or when followed by 3 digits.
  const normalized = t.includes(',')
    ? t.replace(/\./g, '').replace(',', '.')
    : /^\d{1,3}(\.\d{3})+$/.test(t)
      ? t.replace(/\./g, '')
      : t;
  if (!/^-?\d+(\.\d+)?$/.test(normalized)) return null;
  return Number(normalized);
}

export function parseCentavos(text: string): number | null {
  const n = parseDecimal(text);
  return n === null ? null : Math.round(n * 100);
}

export function parseBps(text: string): number | null {
  const n = parseDecimal(text);
  return n === null ? null : Math.round(n * 100);
}

const fmt = (min: number, max: number) =>
  new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: min,
    maximumFractionDigits: max,
    useGrouping: true,
  });

/** 3000 → "30,00" */
export function centavosInput(value: number | null | undefined): string {
  return value === null || value === undefined ? '' : fmt(2, 2).format(value / 100);
}

/** 1250 → "12,5" ; 500 → "5" */
export function bpsInput(value: number | null | undefined): string {
  return value === null || value === undefined ? '' : fmt(0, 2).format(value / 100);
}

/** 0.95 → "0,95" ; 0.01885 → "0,01885" */
export function decimalInput(
  value: number | null | undefined,
  maxDigits = 5,
  minDigits = 2,
): string {
  return value === null || value === undefined ? '' : fmt(minDigits, maxDigits).format(value);
}

/** "17/10/2026" → "2026-10-17" (null if empty or not a real date). */
export function parseBrDate(text: string): string | null {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(text.trim());
  if (!m) return null;
  const iso = `${m[3]}-${m[2]}-${m[1]}`;
  const d = new Date(`${iso}T12:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === iso ? iso : null;
}

/** "3:10" | "3h10" | "3h" | "45min" | "3,5" (hours) → seconds, or null if empty/invalid. */
export function parseDuration(text: string): number | null {
  const t = text.trim().toLowerCase().replace(/\s+/g, '');
  if (t === '') return null;
  const hm = /^(\d+)(?::|h)(\d{1,2})?(?:m|min)?$/.exec(t);
  if (hm) {
    const minutes = Number(hm[2] ?? 0);
    return minutes < 60 ? Number(hm[1]) * 3600 + minutes * 60 : null;
  }
  const min = /^(\d+)(?:m|min)$/.exec(t);
  if (min) return Number(min[1]) * 60;
  const hours = parseDecimal(t);
  return hours === null || hours < 0 ? null : Math.round(hours * 3600);
}

/** 11400 → "3h10" (minutes rounded). */
export function formatHm(seconds: number): string {
  const total = Math.round(seconds / 60);
  return `${Math.floor(total / 60)}h${String(total % 60).padStart(2, '0')}`;
}

/** 11400 → "3:10", for duration inputs. */
export function durationInput(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '';
  const total = Math.round(seconds / 60);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

/** 86 → "86 g"; 12.81 → "12,8 g". */
export function formatGrams(grams: number): string {
  return `${new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 }).format(grams)} g`;
}
